/**
 * Attendance Report — daily attendance + OT for the whole team, with
 * drill-down per staff member, and OT-by-reason summary at the bottom.
 *
 * URL: /admin/reports/ot-reasons   (kept for backwards-compat; sidebar
 * label says "Attendance" now).
 */
import React, { useEffect, useMemo, useState } from 'react';
import axios from 'axios';
import { CalendarDays, Loader2, ChevronDown, ChevronRight, FileDown } from 'lucide-react';
import { useAuth } from '../../../contexts/AuthContext';
import { API } from '../../../lib/api';
import { formatErr, notifyErr, inputCls } from '../../../components/admin/billing/shared';
import { ReportHeader, StatCard } from './_shared';
import EmailReportButton from './EmailReportButton';

const STATUS_GLYPH = {
  present: { ch: 'P', cls: 'bg-emerald-100 text-emerald-700 border-emerald-300' },
  half_day: { ch: 'H', cls: 'bg-amber-100 text-amber-700 border-amber-300' },
  absent: { ch: 'A', cls: 'bg-red-100 text-red-700 border-red-300' },
  week_off: { ch: 'W', cls: 'bg-slate-100 text-slate-500 border-slate-200' },
  paid_leave: { ch: 'PL', cls: 'bg-sky-100 text-sky-700 border-sky-300' },
  unpaid_leave: { ch: 'UL', cls: 'bg-purple-100 text-purple-700 border-purple-300' },
};

const AttendanceReport = () => {
  const { authHeader } = useAuth();
  const now = new Date();
  const [year, setYear] = useState(now.getFullYear());
  const [month, setMonth] = useState(now.getMonth() + 1);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [err, setErr] = useState('');
  const [expanded, setExpanded] = useState(null); // staff_id

  useEffect(() => {
    let alive = true;
    (async () => {
      setLoading(true);
      setErr('');
      try {
        const { data: d } = await axios.get(
          `${API}/reports/attendance?year=${year}&month=${month}`,
          { headers: authHeader() },
        );
        if (alive) setData(d);
      } catch (e) {
        if (alive) setErr(formatErr(e));
      } finally {
        if (alive) setLoading(false);
      }
    })();
    return () => { alive = false; };
  }, [authHeader, year, month]);

  const max = (data?.ot_buckets || []).reduce((m, b) => Math.max(m, b.hours), 0) || 1;

  // Day-of-week header strip
  const daysInMonth = data?.days_in_month || 0;
  const dayHeaders = useMemo(() => {
    const out = [];
    for (let d = 1; d <= daysInMonth; d += 1) {
      try {
        const dt = new Date(data.year, data.month - 1, d);
        out.push({ num: d, dow: dt.toLocaleString('en-IN', { weekday: 'narrow' }) });
      } catch { out.push({ num: d, dow: '' }); }
    }
    return out;
  }, [daysInMonth, data]);

  const downloadStaffPdf = async (sid, name) => {
    try {
      const res = await axios.get(
        `${API}/staff/${sid}/attendance-pdf?year=${year}&month=${month}`,
        { headers: authHeader(), responseType: 'blob', timeout: 60000 },
      );
      const url = URL.createObjectURL(new Blob([res.data], { type: 'application/pdf' }));
      const w = window.open(url, '_blank', 'noopener');
      if (!w) {
        const a = document.createElement('a');
        a.href = url; a.download = `Attendance_${year}-${String(month).padStart(2, '0')}_${name.replace(/\s+/g, '_')}.pdf`;
        document.body.appendChild(a); a.click(); a.remove();
      }
      setTimeout(() => URL.revokeObjectURL(url), 60000);
    } catch (e) {
      notifyErr(e);
    }
  };

  return (
    <div className="p-4 sm:p-6 space-y-4 bg-slate-50 min-h-full" data-testid="report-attendance">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <ReportHeader icon={CalendarDays} title="Attendance Report" />
        <EmailReportButton
          reportType="ot-reasons"
          getFilters={() => ({ year, month })}
        />
      </div>

      <div className="flex flex-wrap items-end gap-2 bg-white border border-slate-200 rounded-xl p-3">
        <select value={year} onChange={(e) => setYear(Number(e.target.value))} className={inputCls} data-testid="attrep-year">
          {Array.from({ length: 5 }).map((_, i) => { const y = now.getFullYear() - 2 + i; return <option key={y} value={y}>{y}</option>; })}
        </select>
        <select value={month} onChange={(e) => setMonth(Number(e.target.value))} className={inputCls} data-testid="attrep-month">
          {Array.from({ length: 12 }, (_, i) => i + 1).map((m) => (
            <option key={m} value={m}>{new Date(2000, m - 1, 1).toLocaleString('default', { month: 'long' })}</option>
          ))}
        </select>
      </div>

      {err && <div className="bg-red-50 border border-red-200 text-red-700 rounded-lg p-3 text-sm">{err}</div>}

      {/* Top tiles */}
      <div className="grid grid-cols-2 sm:grid-cols-6 gap-3">
        <StatCard label="Period" value={data?.month_label || '—'} mono={false} testid="attrep-period" />
        <StatCard label="Team Size" value={data?.total?.headcount ?? 0} testid="attrep-team-size" />
        <StatCard label="Present (man-days)" value={data?.total?.present ?? 0} color="text-emerald-700" testid="attrep-present" />
        <StatCard label="Half day" value={data?.total?.half_day ?? 0} color="text-amber-700" testid="attrep-half" />
        <StatCard label="Absent" value={data?.total?.absent ?? 0} color="text-red-700" testid="attrep-absent" />
        <StatCard label="OT Hours" value={`${data?.total?.ot_hours ?? 0} h`} color="text-teal-700" testid="attrep-ot" />
      </div>

      {/* Per-staff attendance grid */}
      <div className="bg-white border border-slate-200 rounded-xl overflow-hidden">
        <div className="px-4 py-2 border-b border-slate-200 bg-slate-50 flex items-center justify-between">
          <h2 className="text-sm font-semibold text-slate-700">Daily Attendance · per staff</h2>
          <div className="text-[10px] text-slate-500">
            <span className="inline-block w-3 h-3 align-middle mr-1 bg-emerald-100 border border-emerald-300 rounded-sm" />Present
            <span className="inline-block w-3 h-3 align-middle ml-3 mr-1 bg-amber-100 border border-amber-300 rounded-sm" />Half
            <span className="inline-block w-3 h-3 align-middle ml-3 mr-1 bg-red-100 border border-red-300 rounded-sm" />Absent
            <span className="inline-block w-3 h-3 align-middle ml-3 mr-1 bg-slate-100 border border-slate-200 rounded-sm" />Week off
          </div>
        </div>
        {loading ? (
          <div className="p-8 text-center text-slate-500"><Loader2 className="inline animate-spin mr-2" size={16} /> Loading…</div>
        ) : !data || (data.staff || []).length === 0 ? (
          <div className="p-8 text-center text-slate-400 text-sm">No staff configured.</div>
        ) : (
          <div className="overflow-x-auto">
            <table className="text-xs min-w-full">
              <thead className="bg-slate-50 sticky top-0">
                <tr>
                  <th className="text-left px-3 py-2 font-semibold text-slate-600 sticky left-0 bg-slate-50 z-10">Staff</th>
                  {dayHeaders.map((d) => (
                    <th key={d.num} className="px-1 py-2 font-mono font-normal text-slate-400 text-center min-w-[26px]">
                      <div>{d.num}</div>
                      <div className="text-[9px] opacity-70">{d.dow}</div>
                    </th>
                  ))}
                  <th className="px-2 py-2 font-semibold text-emerald-700 text-center">P</th>
                  <th className="px-2 py-2 font-semibold text-amber-700 text-center">½</th>
                  <th className="px-2 py-2 font-semibold text-red-700 text-center">A</th>
                  <th className="px-2 py-2 font-semibold text-teal-700 text-center">OT</th>
                  <th />
                </tr>
              </thead>
              <tbody>
                {data.staff.map((s) => (
                  <React.Fragment key={s.staff_id}>
                    <tr className="border-t border-slate-100 hover:bg-slate-50">
                      <td className="px-3 py-2 font-medium text-slate-800 sticky left-0 bg-white z-10 whitespace-nowrap" data-testid={`attrep-row-${s.staff_id}`}>
                        <button
                          type="button"
                          onClick={() => setExpanded(expanded === s.staff_id ? null : s.staff_id)}
                          className="inline-flex items-center gap-1 hover:text-teal-700"
                          data-testid={`attrep-expand-${s.staff_id}`}
                        >
                          {expanded === s.staff_id ? <ChevronDown size={12} /> : <ChevronRight size={12} />}
                          {s.name}
                        </button>
                        {s.level && <div className="text-[9px] uppercase tracking-wider text-slate-400">{s.level}</div>}
                      </td>
                      {s.daily.map((d) => {
                        const g = STATUS_GLYPH[d.status];
                        const isOt = d.ot_hours > 0;
                        return (
                          <td key={d.date} className="px-0.5 py-1 text-center">
                            {g ? (
                              <span
                                title={`${d.date} (${d.dow}) — ${d.status}${isOt ? ` + ${d.ot_hours}h OT` : ''}`}
                                className={`inline-flex items-center justify-center w-5 h-5 rounded-sm border text-[9px] font-bold ${g.cls} ${isOt ? 'ring-1 ring-teal-400 ring-offset-0' : ''}`}
                              >
                                {g.ch}
                              </span>
                            ) : (
                              <span className="text-slate-300">·</span>
                            )}
                          </td>
                        );
                      })}
                      <td className="px-2 py-1 text-center font-mono font-semibold text-emerald-700">{s.counts.present}</td>
                      <td className="px-2 py-1 text-center font-mono font-semibold text-amber-700">{s.counts.half_day}</td>
                      <td className="px-2 py-1 text-center font-mono font-semibold text-red-700">{s.counts.absent}</td>
                      <td className="px-2 py-1 text-center font-mono font-semibold text-teal-700">{s.counts.ot_hours}h</td>
                      <td className="px-2 py-1 text-right">
                        <button
                          type="button"
                          onClick={() => downloadStaffPdf(s.staff_id, s.name)}
                          className="inline-flex items-center gap-1 text-[10px] text-teal-700 hover:text-teal-900"
                          data-testid={`attrep-pdf-${s.staff_id}`}
                          title="Download this staff's attendance PDF"
                        >
                          <FileDown size={11} /> PDF
                        </button>
                      </td>
                    </tr>
                    {expanded === s.staff_id && (
                      <tr>
                        <td colSpan={daysInMonth + 6} className="bg-slate-50 px-4 py-3">
                          <DrillDown staff={s} />
                        </td>
                      </tr>
                    )}
                  </React.Fragment>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* OT-by-reason (preserved) */}
      <div className="bg-white border border-slate-200 rounded-xl overflow-hidden">
        <div className="px-4 py-2 border-b border-slate-200 bg-slate-50">
          <h2 className="text-sm font-semibold text-slate-700">OT Hours by Reason</h2>
        </div>
        {loading ? null : !data || (data.ot_buckets || []).length === 0 ? (
          <div className="p-6 text-center text-slate-400 text-sm">No overtime recorded in this period.</div>
        ) : (
          <ul className="divide-y divide-slate-100">
            {data.ot_buckets.map((b) => (
              <li key={b.reason} className="px-4 py-3" data-testid={`attrep-bucket-${b.reason.replace(/[^a-z0-9]/gi, '-').slice(0, 30)}`}>
                <div className="flex items-baseline justify-between gap-2 mb-1.5">
                  <div className="font-semibold text-slate-800 text-sm truncate">{b.reason}</div>
                  <div className="text-sm font-mono">
                    <span className="text-amber-700 font-bold">{b.hours} h</span>
                    <span className="text-slate-400 text-xs"> · {b.entries} {b.entries === 1 ? 'entry' : 'entries'}</span>
                  </div>
                </div>
                <div className="h-2.5 bg-slate-100 rounded-full overflow-hidden mb-2">
                  <div className="h-full bg-gradient-to-r from-amber-400 to-amber-600 rounded-full" style={{ width: `${Math.max(2, (b.hours / max) * 100).toFixed(1)}%` }} />
                </div>
                <div className="flex flex-wrap gap-1.5">
                  {b.staff.map((s) => (
                    <span key={s.name} className="text-[11px] bg-slate-100 text-slate-700 px-2 py-0.5 rounded-full">
                      {s.name} <b className="text-amber-700">{s.hours}h</b>
                    </span>
                  ))}
                </div>
              </li>
            ))}
          </ul>
        )}
      </div>
    </div>
  );
};

const DrillDown = ({ staff }) => {
  // Daily breakdown table for one staff
  const days = staff.daily.filter((d) => d.status || d.ot_hours > 0);
  return (
    <div className="space-y-2" data-testid={`attrep-drill-${staff.staff_id}`}>
      <div className="text-xs text-slate-600">
        <b>{staff.name}</b> — Effective days: <b>{staff.counts.effective_days}</b>
        · OT: <b>{staff.counts.ot_hours}h</b> across {staff.counts.ot_entries} entries
      </div>
      {days.length === 0 ? (
        <div className="text-xs text-slate-400">No attendance or OT entries.</div>
      ) : (
        <table className="text-xs w-full bg-white border border-slate-200 rounded">
          <thead className="bg-slate-100 text-slate-600">
            <tr>
              <th className="text-left px-2 py-1">Date</th>
              <th className="text-left px-2 py-1">Day</th>
              <th className="text-left px-2 py-1">Status</th>
              <th className="text-right px-2 py-1">OT (h)</th>
              <th className="text-left px-2 py-1">Notes / OT reason</th>
            </tr>
          </thead>
          <tbody>
            {days.map((d) => (
              <tr key={d.date} className="border-t border-slate-100">
                <td className="px-2 py-1 font-mono text-[11px]">{d.date}</td>
                <td className="px-2 py-1 text-[11px]">{d.dow}</td>
                <td className="px-2 py-1">
                  {d.status ? (
                    <span className={`px-1.5 py-0.5 rounded text-[10px] uppercase font-semibold ${STATUS_GLYPH[d.status]?.cls || 'bg-slate-100 text-slate-600'}`}>
                      {d.status.replace('_', ' ')}
                    </span>
                  ) : '—'}
                </td>
                <td className="px-2 py-1 text-right font-mono">{d.ot_hours || '—'}</td>
                <td className="px-2 py-1 text-slate-600">{d.ot_notes || d.notes || ''}</td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
    </div>
  );
};

export default AttendanceReport;
